/* ============================================================================
 * Desktop — the `#desktop` canvas and its window manager
 * ----------------------------------------------------------------------------
 * Follows the design source, which has NO desktop icons: the desk is a canvas
 * carrying the SUPERCOMPUTE wordmark and the `supercompute.eth` handle as type,
 * with every app reachable from the dock.
 *
 * Launch semantics: a dock click on an already-open app toggles its minimization
 * (restore if hidden, hide if visible).
 * ========================================================================= */

import { useCallback } from 'react';
import { appById } from '../apps/registry';
import { NotBuilt } from '../apps/NotBuilt';
import { OSWindow } from './OSWindow';
import { Taskbar } from './Taskbar';
import { MenuBar } from './MenuBar';
import { useWindowManager } from '../hooks/useWindowManager';
import { useAppearance } from './AppearanceProvider';
import { skinDef } from '../hooks/useSkin';

export function Desktop() {
  const wm = useWindowManager();
  const skin = useAppearance();

  const launch = useCallback(
    (appId: string) => {
      const app = appById(appId);
      if (!app) return;
      const existing = wm.windowFor(appId);
      if (existing) {
        wm.toggleMinimize(existing.id);
        return;
      }
      wm.open({
        appId: app.id,
        title: app.title,
        w: app.defaultSize.w,
        h: app.defaultSize.h,
      });
    },
    [wm],
  );

  return (
    <>
      <MenuBar onLaunch={launch} skin={skin} />
      <div id="desktop" className="wos-crt">
        {/* Wallpaper lockup — type only, no mark. Reads from the active skin,
            so a fork carries its own wordmark (SCHOOL DAO) rather than the
            main skin's. */}
        <div className="wall" aria-hidden="true">
          <div className="wall__lockup">
            <div className="wall__mark">{skinDef(skin.skin).wordmark}</div>
            <div className="wall__ens">{skinDef(skin.skin).handle}</div>
            {skinDef(skin.skin).tagline && (
              <div className="wall__tag">{skinDef(skin.skin).tagline}</div>
            )}
          </div>
        </div>

        <span className="hud-corner hud-corner--tl" aria-hidden="true" />
        <span className="hud-corner hud-corner--tr" aria-hidden="true" />
        <span className="hud-corner hud-corner--bl" aria-hidden="true" />
        <span className="hud-corner hud-corner--br" aria-hidden="true" />

        {wm.windows.map((state) => {
          const app = appById(state.appId);
          if (!app) return null;
          const Body = app.component;
          return (
            <OSWindow
              key={state.id}
              state={state}
              active={wm.activeId === state.id}
              onFocus={() => wm.focus(state.id)}
              onClose={() => wm.close(state.id)}
              onMinimize={() => wm.minimize(state.id)}
              onMove={(x, y) => wm.move(state.id, x, y)}
              onResize={(nw, nh) => wm.resize(state.id, nw, nh)}
            >
              {Body ? <Body /> : <NotBuilt app={app} />}
            </OSWindow>
          );
        })}

        <Taskbar
          onLaunch={launch}
          openAppIds={wm.windows
            .filter((x) => !x.minimized)
            .map((x) => x.appId)}
        />
      </div>
    </>
  );
}
